import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FilterPanel = ({ isOpen, onClose, filters, onFiltersChange }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterChange = (key, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      ageRange: [18, 65],
      distance: 50,
      compatibilityMin: 0,
      zodiacSigns: [],
      musicalPreferences: [],
      onlineOnly: false
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const zodiacSigns = [
    { sign: 'Aries', symbol: '♈' },
    { sign: 'Taurus', symbol: '♉' },
    { sign: 'Gemini', symbol: '♊' },
    { sign: 'Cancer', symbol: '♋' },
    { sign: 'Leo', symbol: '♌' },
    { sign: 'Virgo', symbol: '♍' },
    { sign: 'Libra', symbol: '♎' },
    { sign: 'Scorpio', symbol: '♏' },
    { sign: 'Sagittarius', symbol: '♐' },
    { sign: 'Capricorn', symbol: '♑' },
    { sign: 'Aquarius', symbol: '♒' },
    { sign: 'Pisces', symbol: '♓' }
  ];

  const musicalPreferences = [
    'Harmonic Resonance',
    'Melodic Harmony',
    'Rhythmic Alignment',
    'Tonal Compatibility',
    'Cosmic Frequencies',
    'Planetary Rhythms'
  ];

  const toggleZodiacSign = (sign) => {
    const current = localFilters.zodiacSigns || [];
    const updated = current.includes(sign)
      ? current.filter(s => s !== sign)
      : [...current, sign];
    handleFilterChange('zodiacSigns', updated);
  };

  const toggleMusicalPreference = (preference) => {
    const current = localFilters.musicalPreferences || [];
    const updated = current.includes(preference)
      ? current.filter(p => p !== preference)
      : [...current, preference];
    handleFilterChange('musicalPreferences', updated);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal bg-background/80 backdrop-blur-cosmic">
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-surface shadow-elevation-5">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-heading font-semibold text-text-primary">
              Filters
            </h2>
            <Button
              variant="ghost"
              iconName="X"
              onClick={onClose}
              className="w-8 h-8 p-0"
            />
          </div>

          {/* Filter Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Age Range */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Age Range
              </label>
              <div className="flex items-center space-x-4">
                <Input
                  type="number"
                  value={localFilters.ageRange[0]}
                  onChange={(e) => handleFilterChange('ageRange', [parseInt(e.target.value), localFilters.ageRange[1]])}
                  className="w-20"
                  min="18"
                  max="100"
                />
                <span className="text-text-secondary">to</span>
                <Input
                  type="number"
                  value={localFilters.ageRange[1]}
                  onChange={(e) => handleFilterChange('ageRange', [localFilters.ageRange[0], parseInt(e.target.value)])}
                  className="w-20"
                  min="18"
                  max="100"
                />
              </div>
            </div>

            {/* Distance */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Distance: {localFilters.distance} miles
              </label>
              <input
                type="range"
                min="1"
                max="100"
                value={localFilters.distance}
                onChange={(e) => handleFilterChange('distance', parseInt(e.target.value))}
                className="w-full h-2 bg-surface rounded-full appearance-none slider"
              />
            </div>

            {/* Compatibility Minimum */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Minimum Compatibility: {localFilters.compatibilityMin}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={localFilters.compatibilityMin}
                onChange={(e) => handleFilterChange('compatibilityMin', parseInt(e.target.value))}
                className="w-full h-2 bg-surface rounded-full appearance-none slider"
              />
            </div>

            {/* Zodiac Signs */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Preferred Zodiac Signs
              </label>
              <div className="grid grid-cols-3 gap-2">
                {zodiacSigns.map((zodiac) => (
                  <button
                    key={zodiac.sign}
                    onClick={() => toggleZodiacSign(zodiac.sign)}
                    className={`p-3 rounded-lg border transition-cosmic ${
                      localFilters.zodiacSigns?.includes(zodiac.sign)
                        ? 'bg-primary/20 border-primary text-primary' :'bg-surface/50 border-border text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <div className="text-lg mb-1">{zodiac.symbol}</div>
                    <div className="text-xs">{zodiac.sign}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Musical Preferences */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Musical Preferences
              </label>
              <div className="space-y-2">
                {musicalPreferences.map((preference) => (
                  <button
                    key={preference}
                    onClick={() => toggleMusicalPreference(preference)}
                    className={`w-full p-3 rounded-lg border text-left transition-cosmic ${
                      localFilters.musicalPreferences?.includes(preference)
                        ? 'bg-secondary/20 border-secondary text-secondary' :'bg-surface/50 border-border text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="Music" size={16} color="currentColor" />
                      <span className="text-sm">{preference}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Online Only */}
            <div>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={localFilters.onlineOnly}
                  onChange={(e) => handleFilterChange('onlineOnly', e.target.checked)}
                  className="w-4 h-4 text-primary bg-surface border-border rounded focus:ring-primary"
                />
                <span className="text-sm font-medium text-text-primary">
                  Show online users only
                </span>
              </label>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t border-border">
            <div className="flex space-x-3">
              <Button
                variant="ghost"
                onClick={handleResetFilters}
                fullWidth
              >
                Reset
              </Button>
              <Button
                variant="primary"
                onClick={handleApplyFilters}
                fullWidth
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #4A5FBF;
          cursor: pointer;
        }
        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #4A5FBF;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default FilterPanel;